import { useCallback, useState } from 'react';
import { validationsApi, type ModuleValidationResult } from '@/lib/api';
import { parseApiError } from '@/lib/errorMessages';

export interface PreflightValidationItem {
  moduleKey: string;
  sourceId: string;
}

export interface PreflightProgress {
  done: number;
  total: number;
}

// The server validates a request's modules concurrently, so each chunk is a burst of this many
// GETs at the module platform. 10 matches the mass-migration wave size that Giving has been
// shown to absorb, and keeps every request far inside the client's 3-minute validate timeout.
const CHUNK_SIZE = 10;

export function preflightKey(moduleKey: string, sourceId: string): string {
  return `${moduleKey}::${sourceId}`;
}

/**
 * Runs real module validation for the mass wizard's Pre-flight step by reusing the
 * single-wizard `/validations/modules` endpoint in chunks. Resolves to a map keyed by
 * `preflightKey`, or `null` when a request failed (the message is exposed via `error`).
 */
export function usePreflightValidation() {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<PreflightProgress>({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (items: PreflightValidationItem[]): Promise<Map<string, ModuleValidationResult> | null> => {
      const unique = [...new Map(items.map((i) => [preflightKey(i.moduleKey, i.sourceId), i])).values()];
      setIsRunning(true);
      setError(null);
      setProgress({ done: 0, total: unique.length });

      const results = new Map<string, ModuleValidationResult>();
      try {
        for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
          const chunk = unique.slice(i, i + CHUNK_SIZE);
          const response = await validationsApi.validateModules({ modules: chunk });
          response.results.forEach((r) => results.set(preflightKey(r.moduleKey, r.sourceId), r));
          setProgress({ done: Math.min(i + chunk.length, unique.length), total: unique.length });
        }
        return results;
      } catch (err) {
        setError(parseApiError(err).message);
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [],
  );

  return { run, isRunning, progress, error };
}
